import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { api } from 'src/common/utils/api';

type MeetingActionStatus = 'OPEN' | 'COMPLETED' | 'DORMANT';

export type MeetingAction = {
  id: string;
  eventId: string;
  description: string;
  owner?: string;
  dueDate?: string;
  notes?: string;
  position: number;
  status: MeetingActionStatus;
  isOverdue: boolean;
  completedAt?: string;
  completedBy?: string;
  isUserEdited: boolean;
  isUserAdded: boolean;
};

type MeetingActionsResponse = {
  actions: MeetingAction[];
  hasMeetingNote: boolean;
};

type CreateMeetingActionPayload = {
  description: string;
  owner?: string;
  dueDate?: string;
  notes?: string;
};

type UpdateMeetingActionPayload = {
  description?: string;
  owner?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  completed?: boolean;
};

// null clears the field, undefined leaves it unchanged — matching the backend's absent-vs-null.
export const merged = <T>(value: T | null | undefined, current: T | undefined): T | undefined =>
  value === undefined ? current : (value ?? undefined);

// Mirrors the backend's derived is_overdue so a due-date or completion edit does not flash a
// stale badge until the refetch lands.
const computeIsOverdue = (dueDate: string | undefined, status: MeetingActionStatus): boolean =>
  status === 'OPEN' && dueDate !== undefined && dayjs(dueDate).isBefore(dayjs(), 'day');

export const applyOptimisticUpdate = (
  action: MeetingAction,
  values: UpdateMeetingActionPayload,
): MeetingAction => {
  const status = nextStatus(action, values);
  const dueDate = merged(values.dueDate, action.dueDate);
  return {
    ...action,
    description: values.description ?? action.description,
    owner: merged(values.owner, action.owner),
    dueDate,
    notes: merged(values.notes, action.notes),
    isUserEdited: values.description !== undefined ? true : action.isUserEdited,
    status,
    isOverdue: computeIsOverdue(dueDate, status),
  };
};

const nextStatus = (
  action: MeetingAction,
  values: UpdateMeetingActionPayload,
): MeetingActionStatus => {
  if (values.completed === undefined) {
    return action.status;
  }
  return values.completed ? 'COMPLETED' : 'OPEN';
};

// The first read after a meeting note changes waits on the LLM extracting its actions, which can
// run past the fetch layer's 25 s default on a long note.
const EXTRACTION_TIMEOUT_MS = 120_000;

export const useMeetingActions = (eventId: string, { enabled }: { enabled: boolean }) => {
  const { data, isLoading, isError, error, refetch } = useQuery<MeetingActionsResponse>({
    queryKey: ['calendar-events', eventId, 'actions'],
    enabled,
    retry: false,
    meta: { timeoutMs: EXTRACTION_TIMEOUT_MS },
  });

  return {
    actions: data?.actions,
    hasMeetingNote: data?.hasMeetingNote ?? false,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCreateMeetingAction = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ data: MeetingAction }, Error, CreateMeetingActionPayload>({
    mutationFn: values => api.post<MeetingAction>(`calendar-events/${eventId}/actions`, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId, 'actions'] });
    },
  });
};

export const useUpdateMeetingAction = (eventId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['calendar-events', eventId, 'actions'];

  return useMutation<
    { data: MeetingAction },
    Error,
    { id: string; values: UpdateMeetingActionPayload },
    { previous?: MeetingActionsResponse }
  >({
    mutationFn: ({ id, values }) => api.patch<MeetingAction>(`meeting-actions/${id}`, values),
    // Applied straight to the cache so a tick or an edit lands immediately; the request and
    // its refetch would otherwise leave the row looking unchanged for two round trips.
    onMutate: async ({ id, values }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<MeetingActionsResponse>(queryKey);
      if (previous) {
        queryClient.setQueryData<MeetingActionsResponse>(queryKey, {
          ...previous,
          actions: previous.actions.map(action =>
            action.id === id ? applyOptimisticUpdate(action, values) : action,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useDeleteMeetingAction = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string>({
    mutationFn: id => api.delete(`meeting-actions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId, 'actions'] });
    },
  });
};
